<template>
  <div>
    <custom-result-not-found v-if="documentTypes.length === 0" />

    <div v-if="documentTypes.length > 0">
      <section-title
        class="section-title__extended mt-4"
        content="Listado de Documentos por Tipo de Proveedor"
      />

      <q-tab ref="qTab">
        <q-tab-item
          v-for="(item, index) in documentTypes"
          :key="item.recruitmentTypeId"
          :active="index === 0"
          lazy
          :tabindex="item.recruitmentTypeId"
          :title="item.recruitmentTypeName"
          @click="() => getDocumentTypeForCreation(item.recruitmentTypeId)"
        >
          <div class="d-flex justify-content-end mb-3">
            <action-button
              v-if="canEditDocuments && !addingDocument && !readOnly"
              class="mr-2"
              :class="
                isDragAndDropEnable
                  ? 'custom-reorganize-btn text-white'
                  : 'outline-dark-gray'
              "
              i="far fa-sort-numeric-up"
              :text="
                isDragAndDropEnable
                  ? 'Finalizar Reorganización'
                  : 'Reorganizar Documentos'
              "
              @click="reorganize"
            />
            <action-button
              v-if="
                canEditDocuments &&
                  !addingDocument &&
                  !readOnly &&
                  !isDragAndDropEnable
              "
              i="fas fa-file-plus"
              text="Añadir Documento"
              variant="success"
              @click="() => (addingDocument = true)"
            />
          </div>
          <add-document
            v-if="addingDocument"
            class="mb-3"
            :document-types="documentTypeForCreation"
            @on-cancel="() => (addingDocument = false)"
            @on-create="onAddDocumentHandler($event)"
          />
          <draggable
            v-model="item.documents"
            :disabled="!isDragAndDropEnable"
            force-fallback
            ghost-class="ghost"
          >
            <document-row
              v-for="item in item.documents"
              :key="item.proposalDocumentTypeId"
              :draggable-enabled="isDragAndDropEnable"
              permission-to-actions="provider.biddocumentation.create"
              :read-only="readOnly"
              :value="item"
              @on-delete="onDeleteDocumentHandler($event)"
              @on-update="onUpdateDocumentHandler($event)"
            />
          </draggable>
        </q-tab-item>
      </q-tab>
    </div>
  </div>
</template>
<script>
import SectionTitle from 'SectionTitle';
import CustomResultNotFound from 'custom-result-not-found';
import ActionButton from 'ActionButton';
import { QTab, QTabItem } from '@/components/common/q-tabs';
import {
  getDocumentTypesByAnnouncement as _getDocumentTypesByAnnouncement,
  deleteDocumentTypeOfAnnouncement as _deleteDocumentTypeOfAnnouncement,
  updateDocumentTypeOfAnnouncement as _updateDocumentTypeOfAnnouncement,
  getDocumentTypesOfAnnouncementByRecruitmentType as _getDocumentTypesOfAnnouncementByRecruitmentType,
  addDocumentTypeeToAnnouncement as _addDocumentTypeeToAnnouncement,
  reorderAnnouncementDocuments as _reorderAnnouncementDocuments,
} from '@/modules/proposalProcess/api.js';
import DocumentRow from '@/views/infra/documentConfiguration/components/DocumentRow';
import AddDocument from '@/views/infra/documentConfiguration/components/AddDocument';
import alertMixin from '@/mixins/alert.mixin.js';
import draggable from 'vuedraggable';

export default {
  name: 'DocumentTypes',
  components: {
    SectionTitle,
    CustomResultNotFound,
    ActionButton,
    QTab,
    QTabItem,
    AddDocument,
    DocumentRow,
    draggable,
  },
  mixins: [alertMixin],
  props: {
    announcementId: {
      type: Number,
      default: () => 0,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      currentRecruitmentTypeId: null,
      documentTypes: [],
      documentTypeForCreation: [],
      addingDocument: false,
      isDragAndDropEnable: false,
    };
  },
  computed: {
    canEditDocuments() {
      return this.userHasPermissions('provider.biddocumentation.create');
    },
  },
  async mounted() {
    await this.getDocumentTypesOfAnnouncement();
    if (this.documentTypes[0]?.recruitmentTypeId) {
      this.getDocumentTypeForCreation(this.documentTypes[0]?.recruitmentTypeId);
    }
  },
  methods: {
    async getDocumentTypeForCreation(recruitmentTypeId) {
      this.currentRecruitmentTypeId = recruitmentTypeId;
      this.addingDocument = false;
      this.isDragAndDropEnable = false;
      const { data } = await _getDocumentTypesOfAnnouncementByRecruitmentType(
        this.announcementId,
        recruitmentTypeId
      );
      this.documentTypeForCreation = data;
    },
    async getDocumentTypesOfAnnouncement() {
      const { data } = await _getDocumentTypesByAnnouncement(
        this.announcementId
      );
      this.documentTypes = data;
    },
    async onDeleteDocumentHandler(proposalDocumentTypeId) {
      const confirmed = await this.ShowCancelModal(
        'Confirmación',
        '¿Está seguro desea eliminar el documento a solicitar al someter propuestas?',
        'Si',
        'No'
      );

      if (!confirmed) return;

      await _deleteDocumentTypeOfAnnouncement(
        this.announcementId,
        proposalDocumentTypeId
      );

      this.ShowToast(
        'Proceso Exitoso',
        'El documento ha sido eliminado exitosamente.',
        'success'
      );

      await this.getDocumentTypesOfAnnouncement();
      await this.getDocumentTypeForCreation(this.currentRecruitmentTypeId);

      this.$emit('refresh-detail');
    },
    async onUpdateDocumentHandler(payload) {
      await _updateDocumentTypeOfAnnouncement(this.announcementId, payload);

      this.ShowToast(
        'Proceso Exitoso',
        'El documento ha sido actualizado exitosamente.',
        'success'
      );

      await this.getDocumentTypesOfAnnouncement();
    },
    async onAddDocumentHandler(payload) {
      payload = {
        announcementId: this.announcementId,
        recruitmentTypeId: this.currentRecruitmentTypeId,
        ...payload,
      };

      await _addDocumentTypeeToAnnouncement(payload);

      this.ShowToast(
        'Proceso Exitoso',
        'El documento ha sido añadido exitosamente.',
        'success'
      );

      this.addingDocument = false;
      await this.getDocumentTypesOfAnnouncement();
      await this.getDocumentTypeForCreation(this.currentRecruitmentTypeId);

      this.$emit('refresh-detail');
    },
    async reorganize() {
      if (this.isDragAndDropEnable) {
        let payload = {
          announcementId: this.announcementId,
          recruitmentTypeId: this.currentRecruitmentTypeId,
          documents: this.documentTypes.find(
            (c) => c.recruitmentTypeId == this.currentRecruitmentTypeId
          ).documents,
        };

        await _reorderAnnouncementDocuments(payload);

        this.ShowToast(
          '',
          'Los documentos fueron reorganizados exitosamente.',
          'success'
        );
      }

      this.isDragAndDropEnable = !this.isDragAndDropEnable;
    },
  },
};
</script>

<style scoped>
.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}
.custom-reorganize-btn {
  background-color: #405c64;
}

.outline-dark-gray {
  color: #3f5b60;
  background-color: #ffffff;
  border-color: #3f5b60;
}
</style>
